import React from 'react';
import { Button, Chip, TextField, Tooltip } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';


interface IStoresMultiSelect {
  targets: Array<IBulkPriceTarget>;
  selected: Array<IBulkPriceTarget>;
  isDisabled: boolean;
  // eslint-disable-next-line no-unused-vars
  setSelected: (targets: Array<IBulkPriceTarget>) => void;
  onSelectAll: () => void;
}

const describeTarget = (target: IBulkPriceTarget) => {
  if (!target.eligible) {
    return `${target.label} - ${target.reason}`;
  }

  if (target.kind === 'catalog') {
    const view = target.storeViewName ? `${target.storeCode} (${target.storeViewName})` : target.storeCode;

    return `${target.label} - store view ${view}`;
  }

  return `${target.label} - ${target.storeCode}`;
};

const StoresMultiSelect = ({ targets, selected, isDisabled, setSelected, onSelectAll }: IStoresMultiSelect) => {
  const eligibleTargets = targets.filter((target) => target.eligible);
  const ineligibleCount = targets.length - eligibleTargets.length;

  const handleChange = (event, value) => {
    setSelected(value);
  };

  return (
    <div className="text-left">
      <div className="flex items-center justify-between mb-2">
        <span className="font-bold text-16">Stores to update</span>
        <Button color="primary" size="small" disabled={isDisabled} onClick={onSelectAll}>
          Select all {eligibleTargets.length} stores
        </Button>
      </div>
      <Autocomplete
        multiple
        fullWidth
        disabled={isDisabled}
        value={selected}
        options={eligibleTargets}
        getOptionLabel={(option) => option.label}
        getOptionSelected={(option, value) => option.id === value.id}
        filterSelectedOptions
        renderOption={(option) => <span>{describeTarget(option)}</span>}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.id}
              size="small"
              label={option.label}
              color={option.kind === 'catalog' ? 'primary' : 'default'}
            />
          ))
        }
        onChange={handleChange}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={selected.length ? '' : 'Pick one or more stores'}
            helperText={`${selected.length} of ${eligibleTargets.length} selected`}
          />
        )}
      />
      {ineligibleCount > 0 &&
        <Tooltip
          title={targets.filter((target) => !target.eligible).map(describeTarget).join(' | ')}
          placement="top"
        >
          <span className="text-13 text-muted">
            {ineligibleCount} store(s) cannot be updated and are not listed. Hover to see why.
          </span>
        </Tooltip>
      }
    </div>
  );
};

export default React.memo(StoresMultiSelect);
